import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/dateTime';
import {
  Palmtree,
  Plus,
  Check,
  X,
  Clock,
  Calendar as CalendarIcon,
  Trash2,
  ThermometerSun,
  Star,
  FileCheck,
  AlertCircle,
  Ban,
  RotateCcw,
  History,
  Lock,
  Timer,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import { LockBadge, StornoBadge } from '../components/LockBadge';
import { UnlockRequestDialog } from '../components/UnlockRequestDialog';
import { CorrectionDialog } from '../components/CorrectionDialog';
import { VacationAccountCard } from '../components/VacationAccountCard';
import { VacationReminderBanner } from '../components/VacationReminderBanner';

export default function LeavePage() {
  const { user, api } = useAuth();
  const [myLeaves, setMyLeaves] = useState([]);
  const [certificateTracking, setCertificateTracking] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [leaveCalculation, setLeaveCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Cancellation state (for own cancellation requests)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLeave, setCancelLeave] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  // State for when admin initiated cancellation and employee confirms/rejects
  const [cancelRejectDialogOpen, setCancelRejectDialogOpen] = useState(false);
  const [cancelRejectLeave, setCancelRejectLeave] = useState(null);
  const [cancelRejectReason, setCancelRejectReason] = useState('');

  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });

  // Lock status state
  const [lockStatuses, setLockStatuses] = useState({});
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockEntries, setUnlockEntries] = useState([]);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState(null);
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };

  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    leave_type: 'vacation',
    notes: '',
  });

  const canTrackCertificates = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'accounting';

  // Calculate leave hours when dates change
  useEffect(() => {
    const calculateLeave = async () => {
      if (!formData.start_date || !formData.end_date) {
        setLeaveCalculation(null);
        return;
      }
      
      // Only calculate for vacation type (sick/special don't deduct)
      if (formData.leave_type !== 'vacation') {
        setLeaveCalculation(null);
        return;
      }

      setIsCalculating(true);
      try {
        const response = await api.post(
          `/calendar/calculate-leave?start_date=${formData.start_date}&end_date=${formData.end_date}`
        );
        setLeaveCalculation(response.data);
      } catch (error) {
        console.error('Failed to calculate leave:', error);
        setLeaveCalculation(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateLeave();
  }, [formData.start_date, formData.end_date, formData.leave_type, api]);

  const fetchData = useCallback(async () => {
    try {
      const [leavesRes, statsRes] = await Promise.all([
        // Nur die eigenen Abwesenheiten - fremde werden unter Genehmigungen verwaltet
        api.get('/vacations', { params: { user_id: user?.id } }),
        api.get('/leave/stats'),
      ]);
      setMyLeaves(leavesRes.data);
      setLeaveStats(statsRes.data);

      if (canTrackCertificates) {
        try {
          const certRes = await api.get('/leave/sick/certificate-tracking');
          setCertificateTracking(certRes.data);
        } catch (err) {
          console.error('Certificate tracking not available:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [api, canTrackCertificates, user?.id]);

  // Fetch lock statuses for vacation entries
  const fetchLockStatuses = useCallback(async (vacationEntries) => {
    const ids = vacationEntries.map(e => e.id);
    if (ids.length === 0) return;
    
    try {
      const res = await api.post('/locks/status/bulk', {
        collection_name: 'vacations',
        document_ids: ids,
      });
      const results = {};
      Object.entries(res.data).forEach(([id, status]) => {
        results[id] = status;
      });
      setLockStatuses(results);
    } catch (error) {
      console.error('Failed to fetch lock statuses:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch lock statuses when myLeaves changes
  useEffect(() => {
    if (myLeaves.length > 0) {
      fetchLockStatuses(myLeaves);
    }
  }, [myLeaves, fetchLockStatuses]);

  // Helper to get lock status for an entry
  const getLockStatus = (entryId) => {
    return lockStatuses[entryId] || { level: 'open' };
  };

  // Check if entry can be edited (based on lock level and unlock request)
  const canEditEntry = (entry) => {
    const lockInfo = getLockStatus(entry.id);
    if (lockInfo.level === 'open' || lockInfo.level === 'soft') return true;
    if (lockInfo.level === 'hard' && lockInfo.unlock_request?.status === 'approved') return true;
    return false;
  };

  // Open unlock request dialog
  const openUnlockDialog = (entriesArray) => {
    setUnlockEntries(entriesArray);
    setUnlockDialogOpen(true);
  };

  // Open correction/storno dialog
  const openCorrectionDialog = (entry) => {
    setCorrectionEntry(entry);
    setCorrectionDialogOpen(true);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = () => {
    setFormData({
      start_date: '',
      end_date: '',
      leave_type: 'vacation',
      notes: '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vacations', formData);
      const successMsg = {
        vacation: 'Urlaubsantrag eingereicht',
        sick: 'Krankmeldung erfasst',
        special_leave: 'Sonderurlaubsantrag eingereicht',
      }[formData.leave_type] || 'Abwesenheit erfasst';
      toast.success(successMsg);
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Einreichen';
      toast.error(message);
    }
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm('Abwesenheit löschen?')) return;
    try {
      await api.delete(`/vacations/${leaveId}`);
      toast.success('Abwesenheit gelöscht');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(message);
    }
  };

  // Cancellation handlers
  const openCancelDialog = (leave) => {
    setCancelLeave(leave);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleRequestCancellation = async () => {
    if (!cancelLeave) return;
    try {
      await api.post(`/vacations/${cancelLeave.id}/cancel-request`, {
        reason: cancelReason || undefined,
      });
      toast.success('Stornierungsanfrage gesendet');
      setCancelDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Stornieren';
      toast.error(message);
    }
  };

  const handleConfirmCancellation = async (leaveId) => {
    try {
      await api.post(`/vacations/${leaveId}/cancel-respond`, { action: 'confirm' });
      toast.success('Stornierung bestätigt');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler';
      toast.error(message);
    }
  };

  const openCancelRejectDialog = (leave) => {
    setCancelRejectLeave(leave);
    setCancelRejectReason('');
    setCancelRejectDialogOpen(true);
  };

  const handleWithdrawCancellation = async (leaveId) => {
    try {
      await api.post(`/vacations/${leaveId}/cancel-withdraw`);
      toast.success('Stornierungswunsch zurückgezogen');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };  

  const handleRejectCancellation = async () => {
    if (!cancelRejectLeave) return;
    try {
      await api.post(`/vacations/${cancelRejectLeave.id}/cancel-respond`, {
        action: 'reject',
        reason: cancelRejectReason || undefined,
      });
      toast.success('Stornierung abgelehnt');
      setCancelRejectDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler';
      toast.error(message);
    }
  };

  const handleMarkCertificateReceived = async (leaveId) => {
    try {
      await api.post(`/leave/${leaveId}/mark-certificate-received`);
      toast.success('AU-Bescheinigung als eingegangen markiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const getLeaveTypeLabel = (type) => {
    const labels = { 
      vacation: 'Urlaub', 
      sick: 'Krankheit', 
      special_leave: 'Sonderurlaub',
      special: 'Sonderurlaub', // backward compat
      overtime_reduction: 'Zeitausgleich'
    };
    return labels[type] || type;
  };

  const getLeaveTypeIcon = (type) => {
    if (type === 'sick') return <ThermometerSun className="w-4 h-4" />;
    if (type === 'special_leave' || type === 'special') return <Star className="w-4 h-4" />;
    if (type === 'overtime_reduction') return <Timer className="w-4 h-4" />;
    return <Palmtree className="w-4 h-4" />;
  };

  const getLeaveTypeColor = (type) => {
    if (type === 'sick') return 'bg-red-500/20 text-red-400';
    if (type === 'special_leave' || type === 'special') return 'bg-amber-500/20 text-amber-400';
    if (type === 'overtime_reduction') return 'bg-blue-500/20 text-blue-400';
    return 'bg-emerald-500/20 text-emerald-400';
  };

  const getStatusBadge = (status, workflow) => {
    if (status === 'approved') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"><Check className="w-3 h-3 mr-1" />Genehmigt</span>;
    if (status === 'pending') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"><Clock className="w-3 h-3 mr-1" />Ausstehend</span>;
    if (status === 'rejected') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 border border-red-500/30"><X className="w-3 h-3 mr-1" />Abgelehnt</span>;
    if (status === 'recorded') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30"><FileCheck className="w-3 h-3 mr-1" />Erfasst</span>;
    if (status === 'cancellation_pending') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"><RotateCcw className="w-3 h-3 mr-1" />Storno ausstehend</span>;
    if (status === 'cancelled') return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground border border-border/30"><Ban className="w-3 h-3 mr-1" />Storniert</span>;
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="leave-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Abwesenheiten</h1>
          <p className="text-muted-foreground mt-1">Urlaub, Krankmeldungen & Sonderurlaub</p>
        </div>

        <Button onClick={openCreateDialog} data-testid="create-leave-button" className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary">
          <Plus className="w-4 h-4 mr-2" />
          Abwesenheit erfassen
        </Button>
      </div>

      {/* Vacation Reminder Banner */}
      <VacationReminderBanner />

      {/* Detaillierte Urlaubskontoansicht */}
      <VacationAccountCard year={new Date().getFullYear()} compact={false} />

      {/* Weitere Stats (Krankheit, Sonderurlaub) */}
      {leaveStats && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sick Days */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-glass p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <ThermometerSun className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Krankheitstage</p>
                <p className="text-xs text-muted-foreground">{leaveStats.year}</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-foreground">{leaveStats.sick_days_ytd || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Nicht auf Urlaubskonto</p>
          </motion.div>

          {/* Special Leave */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-glass p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sonderurlaub</p>
                <p className="text-xs text-muted-foreground">{leaveStats.year}</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-foreground">{leaveStats.special_leave_days_ytd || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Nicht auf Urlaubskonto</p>
          </motion.div>

          {/* Total Absences */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-glass p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gesamt Abwesenheit</p>
                <p className="text-xs text-muted-foreground">{leaveStats.year}</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-foreground">{leaveStats.total_absence_days || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Alle Abwesenheitsarten</p>
          </motion.div>
        </div>

        {/* Shutdown Info Banner */}
        {leaveStats.shutdown_periods && leaveStats.shutdown_periods.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-4 p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30"
          >
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-indigo-600 dark:text-indigo-400 font-medium mb-2">Betriebsferien {leaveStats.year}</p>
                <div className="space-y-1">
                  {leaveStats.shutdown_periods.map((period, idx) => (
                    <p key={idx} className="text-sm text-muted-foreground">
                      <span className="text-foreground">{period.name}:</span>{' '}
                      {period.start_date} bis {period.end_date} ({period.days} Tage, {period.hours}h)
                    </p>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Diese Tage werden automatisch von Ihrem Urlaubskonto abgezogen.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </>
      )}

      {/* Meine Urlaubshinweise (vollständige Liste) */}
      <VacationReminderBanner showFull={true} />

      <Tabs defaultValue="my" className="w-full">
        <TabsList className="bg-card/50 border border-border">
          <TabsTrigger value="my" className="data-[state=active]:bg-indigo-500/20">Meine Abwesenheiten</TabsTrigger>
          {canTrackCertificates && certificateTracking.length > 0 && (
            <TabsTrigger value="certificates" className="data-[state=active]:bg-indigo-500/20">
              AU-Tracking ({certificateTracking.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my" className="mt-6">
          <div className="space-y-4">
            {myLeaves.map((leave, index) => {
              const lockInfo = getLockStatus(leave.id);
              const entryEditable = canEditEntry(leave);
              const isStorno = leave.is_correction;
              const isLocked = lockInfo.level === 'hard' || lockInfo.level === 'final';
              // Stornierungswunsch offen: Status bleibt 'approved', Kennzeichen am Eintrag (Altbestand: eigener Status)
              const cancelPending = leave.cancellation_pending || leave.status === 'cancellation_pending';              
              
              return (
              <motion.div
                key={leave.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card-glass p-4 md:p-6 ${isStorno ? 'opacity-60' : ''}`}
                data-testid={`leave-item-${leave.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getLeaveTypeColor(leave.leave_type || leave.vacation_type)}`}>
                      {getLeaveTypeIcon(leave.leave_type || leave.vacation_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`text-foreground font-medium ${isStorno ? 'line-through' : ''}`}>{getLeaveTypeLabel(leave.leave_type || leave.vacation_type)}</p>
                        <LockBadge lockInfo={lockInfo} size="sm" />
                        {isStorno && <StornoBadge entry={leave} />}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {safeFormatDate(leave.start_date, 'd. MMM')} - {safeFormatDate(leave.end_date, 'd. MMM yyyy')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-mono font-bold text-foreground">{leave.days} Tage</p>
                    </div>
                    {getStatusBadge(cancelPending ? 'cancellation_pending' : leave.status, leave.workflow)}
                    {/* Delete button - only if editable */}
                    {entryEditable && (leave.status === 'pending' || leave.status === 'recorded') && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(leave.id)} className="text-muted-foreground hover:text-red-600 dark:text-red-400" data-testid={`delete-leave-${leave.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Storno request button - show for approved vacations if NOT final-locked
                        Der Stornierungs-Workflow ist bei HARD-Sperre erlaubt (workflow_action="cancellation")
                        weil er seine eigene beidseitige Bestätigung hat */}
                    {leave.status === 'approved' && !cancelPending && (leave.leave_type || leave.vacation_type) !== 'sick' && lockInfo.level !== 'final' && (
                      <Button
                        size="sm"
                        onClick={() => openCancelDialog(leave)}
                        data-testid={`cancel-leave-${leave.id}`}
                        className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                        title="Urlaub stornieren - Der Urlaub findet nicht statt"
                      >
                        <Ban className="w-3 h-3 mr-1" />
                        Stornieren
                      </Button>
                    )}
                    {/* FINAL-locked: Show disabled hint instead of Storno button */}
                    {leave.status === 'approved' && !cancelPending && (leave.leave_type || leave.vacation_type) !== 'sick' && lockInfo.level === 'final' && (
                      <span 
                        className="px-2 py-1 text-xs text-muted-foreground bg-muted/50 rounded border border-border/50 cursor-not-allowed"
                        title="Dieser Eintrag wurde bereits an DATEV übergeben. Eine Stornierung ist nur noch über die Buchhaltung möglich."
                      >
                        Bereits abgerechnet
                      </span>
                    )}
                    {/* Unlock request button for hard-locked entries - for data corrections */}
                    {!entryEditable && lockInfo.level === 'hard' && lockInfo.can_request_unlock && (
                      <Button
                        size="sm"
                        onClick={() => openUnlockDialog([leave])}
                        className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                        data-testid={`unlock-leave-${leave.id}`}
                        title="Entsperrung beantragen - Die Daten sind falsch und sollen korrigiert werden (z.B. falsches Datum)"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        Entsperrung
                      </Button>
                    )}
                    {/* Admin Storno for final-locked entries */}
                    {lockInfo.level === 'final' && (user?.role === 'admin' || user?.role === 'accounting') && (
                      <Button
                        size="sm"
                        onClick={() => openCorrectionDialog(leave)}
                        className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                        data-testid={`storno-leave-${leave.id}`}
                        title="Buchhalterischen Storno erstellen - erzeugt einen Gegeneintrag"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Storno
                      </Button>
                    )}
                    {/* History button - always visible */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => showHistory('vacations', leave.id)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-300"
                      data-testid={`history-leave-${leave.id}`}
                    >
                      <History className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Medical certificate info for sick leave */}
                {(leave.leave_type === 'sick') && leave.medical_certificate_required && (
                  <div className={`mt-3 p-3 rounded-lg ${leave.medical_certificate_received ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                    <div className="flex items-center gap-2">
                      {leave.medical_certificate_received ? (
                        <>
                          <FileCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-sm text-emerald-600 dark:text-emerald-400">AU-Bescheinigung eingegangen</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          <span className="text-sm text-amber-600 dark:text-amber-400">AU-Bescheinigung erforderlich (ab 3 Tagen)</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {leave.notes && (
                  <p className="mt-3 text-sm text-muted-foreground">{leave.notes}</p>
                )}

                {leave.rejection_reason && (
                  <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-sm text-red-600 dark:text-red-400">Ablehnung: {leave.rejection_reason}</p>
                  </div>
                )}

                {cancelPending && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      <RotateCcw className="w-3 h-3 inline mr-1" />
                      Stornierung angefragt
                      {leave.cancellation_reason && `: ${leave.cancellation_reason}`}
                    </p>
                    {leave.cancellation_initiator_role === 'admin' && (
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => handleConfirmCancellation(leave.id)}
                          data-testid={`confirm-cancel-${leave.id}`}
                          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                        >
                          <Check className="w-3 h-3 mr-1" />Bestätigen
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openCancelRejectDialog(leave)}
                          data-testid={`reject-cancel-${leave.id}`}
                          className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                        >
                          <X className="w-3 h-3 mr-1" />Ablehnen
                        </Button>
                      </div>
                    )}
                    {leave.cancellation_initiator_role === 'employee' && (
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-xs text-muted-foreground">Warte auf Bestätigung durch Admin/Vorgesetzten</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleWithdrawCancellation(leave.id)}
                          data-testid={`withdraw-cancel-${leave.id}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />Zurückziehen
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
              );
            })}

            {myLeaves.length === 0 && (
              <div className="text-center py-16">
                <Palmtree className="w-12 h-12 text-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Keine Abwesenheiten</h3>
                <p className="text-muted-foreground">Sie haben noch keine Abwesenheit erfasst.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {canTrackCertificates && (
          <TabsContent value="certificates" className="mt-6">
            <div className="space-y-4">
              <div className="card-glass p-4 bg-amber-500/5 border-amber-500/20">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">Ausstehende AU-Bescheinigungen</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Krankmeldungen ab 3 Tagen erfordern eine AU-Bescheinigung</p>
              </div>

              {certificateTracking.map((leave, index) => (
                <motion.div
                  key={leave.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="card-glass p-4 md:p-6"
                  data-testid={`cert-tracking-${leave.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                        <ThermometerSun className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-foreground font-medium">
                          {leave.user?.first_name} {leave.user?.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {safeFormatDate(leave.start_date, 'd. MMM')} - {safeFormatDate(leave.end_date, 'd. MMM yyyy')}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">AU-Bescheinigung ausstehend</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-2xl font-mono font-bold text-foreground">{leave.days} Tage</p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleMarkCertificateReceived(leave.id)} 
                        data-testid={`mark-cert-${leave.id}`}
                        className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        Eingegangen
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {certificateTracking.length === 0 && (
                <div className="text-center py-16">
                  <FileCheck className="w-12 h-12 text-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">Alle AU-Bescheinigungen vorhanden</h3>
                  <p className="text-muted-foreground">Keine ausstehenden AU-Bescheinigungen.</p>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Abwesenheit erfassen</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Art der Abwesenheit</Label>
              <Select value={formData.leave_type} onValueChange={(value) => setFormData({ ...formData, leave_type: value })}>
                <SelectTrigger className="bg-input border-border text-foreground" data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="vacation">
                    <div className="flex items-center gap-2">
                      <Palmtree className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span>Urlaub</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="sick">
                    <div className="flex items-center gap-2">
                      <ThermometerSun className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span>Krankmeldung</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="special_leave">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span>Sonderurlaub</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="overtime_reduction">
                    <div className="flex items-center gap-2">
                      <Timer className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span>Zeitausgleich</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {formData.leave_type === 'sick' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Krankmeldungen werden sofort erfasst (keine Genehmigung erforderlich). 
                  Ab dem ersten Tag ist eine AU-Bescheinigung erforderlich.
                </p>
              )}
              {formData.leave_type === 'special_leave' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Sonderurlaub muss vom Vorgesetzten genehmigt werden.
                </p>
              )}
              {formData.leave_type === 'overtime_reduction' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Zeitausgleich wird vom Vorgesetzten genehmigt. Die Tage werden nicht vom Urlaubskonto 
                  abgezogen – stattdessen reduziert sich dein Zeitguthaben entsprechend.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Von</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-input border-border text-foreground hover:bg-card" data-testid="start-date-picker">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {safeFormatDate(formData.start_date, 'd. MMM yyyy', 'Datum')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.start_date ? new Date(formData.start_date) : undefined}
                      onSelect={(date) => setFormData({ ...formData, start_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                      locale={de}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Bis</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-input border-border text-foreground hover:bg-card" data-testid="end-date-picker">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {safeFormatDate(formData.end_date, 'd. MMM yyyy', 'Datum')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.end_date ? new Date(formData.end_date) : undefined}
                      onSelect={(date) => setFormData({ ...formData, end_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                      locale={de}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Leave Calculation Display */}
            {formData.leave_type === 'vacation' && formData.start_date && formData.end_date && (
              <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20 space-y-3">
                {isCalculating ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Berechne Urlaubsabzug...</span>
                  </div>
                ) : leaveCalculation ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Urlaubsabzug:</span>
                      <span className="text-xl font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {leaveCalculation.total_hours}h
                        <span className="text-sm font-normal text-muted-foreground ml-2">
                          (≈ {leaveCalculation.total_days_equivalent} Tage)
                        </span>
                      </span>
                    </div>
                    
                    {leaveCalculation.has_reduced_days && (
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
                        <CalendarIcon className="w-4 h-4" />
                        <span>Kurzarbeitstag(e) berücksichtigt (z.B. 24.12., 31.12.)</span>
                      </div>
                    )}
                    
                    {leaveCalculation.has_shutdown_days && (
                      <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-sm">
                        <CalendarIcon className="w-4 h-4" />
                        <span>Betriebsferien: {leaveCalculation.shutdown_hours}h werden automatisch abgezogen</span>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-muted-foreground">Notiz (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Optionale Bemerkungen..."
                rows={3}
                data-testid="leave-notes"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-muted-foreground hover:text-foreground">Abbrechen</Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="submit-leave-button">
                {formData.leave_type === 'sick' ? 'Erfassen' : 'Beantragen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancellation Request Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Urlaub stornieren</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {cancelLeave && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-sm text-muted-foreground">
                  {cancelLeave.user?.first_name ? `${cancelLeave.user.first_name} ${cancelLeave.user.last_name} - ` : ''}
                  {safeFormatDate(cancelLeave.start_date, 'd. MMM')} - {safeFormatDate(cancelLeave.end_date, 'd. MMM yyyy')}
                  {' '}({cancelLeave.days} Tage)
                </p>
              </div>
            )}

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Die Stornierung muss von der anderen Seite bestätigt werden, bevor der Urlaub storniert wird.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund (optional)</Label>
              <Textarea
                data-testid="cancel-reason-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum soll der Urlaub storniert werden?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setCancelDialogOpen(false)} className="text-muted-foreground hover:text-foreground">
              Abbrechen
            </Button>
            <Button
              onClick={handleRequestCancellation}
              data-testid="confirm-cancel-request-button"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Ban className="w-4 h-4 mr-2" />
              Stornierung anfragen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Rejection Dialog */}
      <Dialog open={cancelRejectDialogOpen} onOpenChange={setCancelRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Stornierung ablehnen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">Bitte geben Sie einen Grund für die Ablehnung der Stornierung an:</p>
            <Textarea
              data-testid="cancel-reject-reason-input"
              value={cancelRejectReason}
              onChange={(e) => setCancelRejectReason(e.target.value)}
              className="bg-input border-border text-foreground resize-none"
              placeholder="Grund für die Ablehnung..."
              rows={4}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setCancelRejectDialogOpen(false)} className="text-muted-foreground hover:text-foreground">
              Abbrechen
            </Button>
            <Button onClick={handleRejectCancellation} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-cancel-reject-button">
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock Request Dialog */}
      <UnlockRequestDialog
        open={unlockDialogOpen}
        onOpenChange={setUnlockDialogOpen}
        entries={unlockEntries}
        collectionName="vacations"
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Correction/Storno Dialog */}
      <CorrectionDialog
        open={correctionDialogOpen}
        onOpenChange={setCorrectionDialogOpen}
        entry={correctionEntry}
        collectionName="vacations"
        onSuccess={() => {
          fetchData();
        }}
      />
    </div>
  );
}
